import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Eye, Info } from "lucide-react";
import { fetchPosts } from "../../lib/api";
import { Reveal, SectionHead } from "./Reveal";

const LeakCard = ({ post, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * 0.08}>
      <article data-testid={`leak-card-${i}`}
        className="group relative h-full overflow-hidden rounded-3xl border border-cyan-400/12 bg-[#0F1829] p-6 sm:p-8 hover:border-cyan-400/40 transition-colors duration-500">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/60 to-transparent" />
        <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em]">
          <span className="text-[#F59E0B]">Leak #{String(i + 1).padStart(3, "0")}</span>
          <span className="rounded-full border border-cyan-400/30 px-2.5 py-1 text-[#00E5FF]">{post.tag || "Sneak peek"}</span>
        </div>
        <h3 className="mt-6 font-display text-2xl sm:text-3xl font-extrabold uppercase text-white">{post.title}</h3>
        <div className="relative mt-4">
          <p className={`text-sm sm:text-base text-slate-300 whitespace-pre-line transition-[filter,opacity] duration-700 ${open ? "" : "blur-[6px] opacity-60 select-none"}`}>
            {post.content}
          </p>
        </div>
        {post.image_url && open && (
          <motion.img initial={{ opacity: 0 }} animate={{ opacity: 1 }} src={post.image_url} alt={post.title}
            className="mt-5 aspect-video w-full rounded-2xl object-cover" />
        )}
        <button onClick={() => setOpen((o) => !o)} data-testid={`leaks-unredact-button-${i}`}
          className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#00E5FF] hover:text-white transition-colors">
          {open ? <Lock className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {open ? "Hide" : "Reveal"}
        </button>
      </article>
    </Reveal>
  );
};

const Placeholder = ({ i }) => (
  <div className="relative overflow-hidden rounded-3xl border border-cyan-400/12 bg-[#0F1829] p-8" data-testid={`leak-placeholder-${i}`}>
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/60 to-transparent" />
    <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Leak #{String(i + 1).padStart(3, "0")}</p>
    <div className="mt-6 space-y-3">
      <div className="h-5 w-3/4 rounded bg-cyan-400/15" />
      <div className="h-3 w-full rounded bg-cyan-400/10" />
      <div className="h-3 w-5/6 rounded bg-cyan-400/10" />
    </div>
    <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Next leak incoming</p>
  </div>
);

export const Leaks = () => {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    fetchPosts("leak").then(setPosts);
  }, []);
  const list = posts ?? [];
  return (
    <section id="leaks" className="relative border-t border-cyan-400/10 py-24 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead over="// 02 — Coming soon" title={<>Leaks<span className="text-[#00E5FF]">.</span></>}
          sub="Early looks at what we're building next. Tap to reveal." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((p, i) => <LeakCard key={p.id} post={p} i={i} />)}
          {list.length === 0 && [0, 1, 2].map((i) => <Placeholder key={i} i={i} />)}
        </div>
        <Reveal>
          <p data-testid="leaks-disclaimer"
            className="mt-8 flex items-start gap-3 rounded-2xl border border-[#F59E0B]/25 bg-[#F59E0B]/5 px-5 py-4 text-sm text-slate-300">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#F59E0B]" />
            <span><span className="font-mono text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Heads up — </span>Leaks are early looks at ideas in progress. Not every leak will make it into the game, and features may change before release.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
};
